import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { getProfile, logout as apiLogout } from "@/api/auth";
import type { AdminUser } from "@/types";

type AuthContextType = {
  user: AdminUser | null;
  loading: boolean;
  fetchProfile: () => Promise<void>;
  handleLogout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProfile = useCallback(async () => {
    try {
      const res = await getProfile();
      setUser(res.data as AdminUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleLogout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // 即使后端注销失败，前端仍跳转
    } finally {
      setUser(null);
      message.success("已安全退出");
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, loading, fetchProfile, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
